/**
 * The arithmetic behind the classroom noise meter.
 *
 * The browser hands over raw microphone samples; this module turns them into a relative
 * level. Levels are decibels relative to full scale (dBFS, where 0 is the loudest signal
 * the microphone chain can carry), not sound pressure: a laptop and a phone with the same
 * room noise show different numbers, which is why the meter has a sensitivity control and
 * why nothing here claims to be calibrated dB SPL. Samples are never stored; only these
 * running numbers are kept.
 */

/** The meter reads empty at this level and full at `CEILING_DB`. */
export const FLOOR_DB = -70;
export const CEILING_DB = -10;
/** The lowest level reported, for silence. */
export const SILENCE_DB = -120;
export const SENSITIVITY_RANGE = 20;
export const MIN_GAP = 5;
export const DEFAULT_QUIET = 35;
export const DEFAULT_LOUD = 70;
/** How slowly the bar falls, as a time constant in milliseconds. */
export const RELEASE_MS = 400;
/** How long the peak marker stays put. */
export const PEAK_HOLD_MS = 2000;
export const HISTORY_STEP_MS = 500;
export const HISTORY_LENGTH = 120;

export type Zone = "quiet" | "talk" | "loud";

export interface Settings {
  /** Decibels added before scaling: positive makes the meter more sensitive. */
  sensitivity: number;
  /** The bar is "quiet" below this percentage. */
  quietBelow: number;
  /** The bar is "too loud" from this percentage. */
  loudAbove: number;
}

export const DEFAULT_SETTINGS: Settings = {
  sensitivity: 0,
  quietBelow: DEFAULT_QUIET,
  loudAbove: DEFAULT_LOUD,
};

/** Root-mean-square of a block of samples in the range −1 to 1. */
export function rmsOf(samples: readonly number[] | Float32Array): number {
  if (samples.length === 0) {
    return 0;
  }
  let sum = 0;
  for (const value of samples) {
    sum += value * value;
  }
  return Math.sqrt(sum / samples.length);
}

/** dBFS for an RMS value, floored at silence. */
export function toDb(rms: number): number {
  if (!(rms > 0)) {
    return SILENCE_DB;
  }
  return Math.max(SILENCE_DB, 20 * Math.log10(rms));
}

const clamp = (value: number, low: number, high: number): number =>
  Math.min(high, Math.max(low, value));

/** Map dBFS to the 0–100 bar, after the sensitivity adjustment. */
export function levelFromDb(db: number, sensitivity: number): number {
  const adjusted =
    db + clamp(sensitivity, -SENSITIVITY_RANGE, SENSITIVITY_RANGE);
  return clamp(((adjusted - FLOOR_DB) / (CEILING_DB - FLOOR_DB)) * 100, 0, 100);
}

export function zoneOf(
  level: number,
  quietBelow: number,
  loudAbove: number
): Zone {
  if (level >= loudAbove) {
    return "loud";
  }
  return level < quietBelow ? "quiet" : "talk";
}

/** Rise at once, fall gradually: a bar that can be read as well as seen. */
export function follow(previous: number, next: number, dtMs: number): number {
  if (next >= previous) {
    return next;
  }
  return next + (previous - next) * Math.exp(-Math.max(0, dtMs) / RELEASE_MS);
}

/** Keep the thresholds in order and a gap apart; the one just moved wins. */
export function withThreshold(
  settings: Settings,
  which: "quietBelow" | "loudAbove",
  value: number
): Settings {
  const v = Math.round(value);
  if (which === "quietBelow") {
    const quietBelow = clamp(v, MIN_GAP, 100 - MIN_GAP);
    return {
      ...settings,
      quietBelow,
      loudAbove: Math.max(settings.loudAbove, quietBelow + MIN_GAP),
    };
  }
  const loudAbove = clamp(v, 2 * MIN_GAP, 100);
  return {
    ...settings,
    loudAbove,
    quietBelow: Math.min(settings.quietBelow, loudAbove - MIN_GAP),
  };
}

/** Milliseconds spent in each zone this session. */
export type ZoneTime = Record<Zone, number>;

export interface Meter {
  /** The smoothed bar, 0–100. */
  level: number;
  peak: number;
  peakAgeMs: number;
  /** Unbroken time in the loud zone. */
  loudMs: number;
  zone: Zone;
  time: ZoneTime;
  /** A level every `HISTORY_STEP_MS`, oldest first. */
  history: number[];
  historyMs: number;
}

export function emptyMeter(): Meter {
  return {
    level: 0,
    peak: 0,
    peakAgeMs: 0,
    loudMs: 0,
    zone: "quiet",
    time: { quiet: 0, talk: 0, loud: 0 },
    history: [],
    historyMs: 0,
  };
}

function advancePeak(
  meter: Meter,
  level: number,
  dtMs: number
): Pick<Meter, "peak" | "peakAgeMs"> {
  if (level >= meter.peak) {
    return { peak: level, peakAgeMs: 0 };
  }
  const peakAgeMs = meter.peakAgeMs + dtMs;
  return peakAgeMs > PEAK_HOLD_MS
    ? { peak: level, peakAgeMs: 0 }
    : { peak: meter.peak, peakAgeMs };
}

function advanceHistory(
  meter: Meter,
  level: number,
  dtMs: number
): Pick<Meter, "history" | "historyMs"> {
  const historyMs = meter.historyMs + dtMs;
  if (historyMs < HISTORY_STEP_MS) {
    return { history: meter.history, historyMs };
  }
  return {
    history: [...meter.history, level].slice(-HISTORY_LENGTH),
    historyMs: historyMs - HISTORY_STEP_MS,
  };
}

/** One reading of `rawLevel` (0–100, unsmoothed) after `dtMs` milliseconds. */
export function advance(
  meter: Meter,
  rawLevel: number,
  dtMs: number,
  settings: Settings
): Meter {
  const dt = Math.max(0, dtMs);
  const level = follow(meter.level, rawLevel, dt);
  const zone = zoneOf(level, settings.quietBelow, settings.loudAbove);
  return {
    level,
    zone,
    ...advancePeak(meter, level, dt),
    loudMs: zone === "loud" ? meter.loudMs + dt : 0,
    time: { ...meter.time, [zone]: meter.time[zone] + dt },
    ...advanceHistory(meter, level, dt),
  };
}

/** Whole-number shares of the session spent in each zone, adding to 100 (all zero if no time). */
export function zoneShares(time: ZoneTime): ZoneTime {
  const total = time.quiet + time.talk + time.loud;
  if (total <= 0) {
    return { quiet: 0, talk: 0, loud: 0 };
  }
  const quiet = Math.round((time.quiet / total) * 100);
  const loud = Math.round((time.loud / total) * 100);
  return { quiet, loud, talk: Math.max(0, 100 - quiet - loud) };
}

/** Whether the warning should show: loud for `afterSeconds` without a break (0 means off). */
export function warning(meter: Meter, afterSeconds: number): boolean {
  return afterSeconds > 0 && meter.loudMs >= afterSeconds * 1000;
}
